/**
 * Brochure Builder — v1 templated pitch + highlight-callout picker.
 *
 * Deliberately NOT AI generation: a features-priority list per vehicle
 * type ranks the dealer's already-scraped `features` array, then a
 * fixed sentence template slots in trim + drivetrain + top features.
 * No new backend fields required — everything here is derived from
 * data the scraper already returns.
 */
import {
  Truck, Compass, Flame, ShieldCheck, Video, MapPin, Smartphone, Sun,
  Armchair, Users, ArrowUpDown, Music, Eye, Key, CircleDot, Sparkles,
} from "lucide-react";

const RULES = [
  { id: "tow", keywords: ["tow"], icon: Truck },
  { id: "fourwd", keywords: ["4wd", "4x4", "four-wheel", "four wheel", "awd", "all-wheel", "all wheel"], icon: Compass },
  { id: "heated_seats", keywords: ["heated seat"], icon: Flame },
  { id: "heated_steering", keywords: ["heated steering"], icon: Flame },
  { id: "safety_tech", keywords: ["collision", "blind spot", "lane departure", "lane keep", "emergency braking", "forward collision"], icon: ShieldCheck },
  { id: "backup_camera", keywords: ["camera", "rear view"], icon: Video },
  { id: "navigation", keywords: ["navigation", "nav system"], icon: MapPin },
  { id: "carplay", keywords: ["apple carplay", "android auto", "carplay"], icon: Smartphone },
  { id: "sunroof", keywords: ["sunroof", "moonroof", "panoramic roof"], icon: Sun },
  { id: "leather", keywords: ["leather"], icon: Armchair },
  { id: "third_row", keywords: ["third row", "third-row", "captain"], icon: Users },
  { id: "power_liftgate", keywords: ["power liftgate", "power tailgate"], icon: ArrowUpDown },
  { id: "premium_audio", keywords: ["premium sound", "premium audio", "bose", "harman"], icon: Music },
  { id: "headup", keywords: ["head-up display", "head up display", "hud"], icon: Eye },
  { id: "keyless", keywords: ["keyless"], icon: Key },
  { id: "alloy", keywords: ["alloy wheel"], icon: CircleDot },
];

// Table-stakes today — never picked as a headline highlight, but stay
// in the full features checklist untouched.
const EXCLUDE_KEYWORDS = [
  "bluetooth", "usb", "aux ", "am/fm", "cd player", "cruise control",
  "power window", "power lock", "air condition", "tire pressure",
];

const CATEGORY_ORDER = {
  truck: ["tow", "fourwd", "safety_tech", "heated_seats", "backup_camera", "navigation",
    "carplay", "heated_steering", "keyless", "sunroof", "leather", "premium_audio",
    "headup", "power_liftgate", "third_row", "alloy"],
  suv: ["third_row", "power_liftgate", "safety_tech", "heated_seats", "sunroof", "fourwd",
    "navigation", "carplay", "leather", "backup_camera", "heated_steering", "premium_audio",
    "headup", "tow", "keyless", "alloy"],
  car: ["heated_seats", "sunroof", "leather", "safety_tech", "navigation", "carplay",
    "premium_audio", "headup", "keyless", "backup_camera", "heated_steering", "fourwd",
    "alloy", "tow", "third_row", "power_liftgate"],
  default: ["safety_tech", "heated_seats", "navigation", "carplay", "sunroof", "leather",
    "backup_camera", "fourwd", "tow", "third_row", "power_liftgate", "premium_audio",
    "headup", "keyless", "alloy"],
};

function categoryFor(bodyStyle) {
  const b = String(bodyStyle || "").toLowerCase();
  if (b.includes("truck") || b.includes("pickup")) return "truck";
  if (b.includes("suv") || b.includes("crossover")) return "suv";
  if (["sedan", "coupe", "hatchback", "convertible", "wagon"].some((k) => b.includes(k))) return "car";
  return "default";
}

function isExcluded(text) {
  const t = text.toLowerCase();
  return EXCLUDE_KEYWORDS.some((kw) => t.includes(kw));
}

// ─────────────────── Drivetrain — single source of truth ───────────────────
//
// Both the specs table AND this pitch generator must read the SAME mapped
// value — this is exactly the kind of field that drifts if normalized in
// two places. Known schema.org driveWheelConfiguration values (bare or as a
// full `https://schema.org/...` URI) map to plain English. Anything
// unrecognized falls back to a cleaned-up string — the raw URL/identifier
// is NEVER shown to the customer, even as a last resort.
const DRIVETRAIN_MAP = {
  allwheeldriveconfiguration: "All-Wheel Drive",
  fourwheeldriveconfiguration: "Four-Wheel Drive",
  frontwheeldriveconfiguration: "Front-Wheel Drive",
  rearwheeldriveconfiguration: "Rear-Wheel Drive",
  awd: "All-Wheel Drive",
  "4wd": "Four-Wheel Drive",
  "4x4": "Four-Wheel Drive",
  fwd: "Front-Wheel Drive",
  rwd: "Rear-Wheel Drive",
};

export function normalizeDrivetrain(raw) {
  if (!raw) return raw;
  const s = String(raw).trim();
  const tail = s.includes("/") ? s.split("/").pop() : s;
  const key = tail.trim().toLowerCase();
  if (DRIVETRAIN_MAP[key]) return DRIVETRAIN_MAP[key];
  // Last-resort fallback — strip the schema.org prefix/suffix and add word
  // spacing so an unmapped value reads like text, never a bare URL.
  let cleaned = tail.replace(/^https?:\/\//i, "")
    .replace(/configuration$/i, "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim();
  return cleaned || s;
}

function matchRule(text) {
  const t = text.toLowerCase();
  return RULES.find((r) => r.keywords.some((kw) => t.includes(kw)));
}

export function rankFeatures(features, bodyStyle) {
  const order = CATEGORY_ORDER[categoryFor(bodyStyle)] || CATEGORY_ORDER.default;
  const scored = [];
  (features || []).forEach((f, idx) => {
    if (!f || isExcluded(f)) return;
    const rule = matchRule(f);
    const orderPos = rule ? order.indexOf(rule.id) : -1;
    scored.push({
      text: f,
      icon: rule ? rule.icon : Sparkles,
      rank: orderPos === -1 ? 500 : orderPos,
      idx,
    });
  });
  scored.sort((a, b) => (a.rank - b.rank) || (a.idx - b.idx));
  return scored;
}

export function buildPitchAndHighlights(vehicle) {
  const ranked = rankFeatures(vehicle.features, vehicle.body_style);
  const highlights = ranked.slice(0, 4).map((r) => ({ label: r.text, icon: r.icon }));
  const top2 = ranked.slice(0, 2).map((r) => r.text);

  const model = vehicle.model || "vehicle";
  const trim = vehicle.trim;
  const drivetrainDisplay = normalizeDrivetrain(vehicle.drivetrain);
  const drivetrainLower = drivetrainDisplay ? drivetrainDisplay.toLowerCase() : null;
  const trimPhrase = trim ? ` pairs its ${trim} trim` : ` pairs`;
  const drivePhrase = drivetrainLower ? ` with confident ${drivetrainLower}.` : ".";

  let pitch;
  if (top2.length === 2) {
    pitch = `Well-equipped with ${top2[0]} and ${top2[1].toLowerCase()}, this ${model}` +
      (trim || drivetrainLower ? `${trimPhrase}${drivePhrase}` : " is ready to impress.");
  } else if (top2.length === 1) {
    pitch = `Featuring ${top2[0]}, this ${model}` +
      (trim || drivetrainLower ? `${trimPhrase}${drivePhrase}` : " is ready to impress.");
  } else {
    pitch = `This ${model}${trim ? ` ${trim}` : ""}` +
      (drivetrainLower ? ` comes with confident ${drivetrainLower}` : "") +
      ` is ready for its next owner.`;
  }

  return { pitch, highlights };
}
